<template>
  <Transition name="fade-up">
    <div v-if="visible" class="cookie-banner" role="dialog" aria-labelledby="cookie-title" aria-describedby="cookie-description" aria-modal="false">
      <div class="cookie-banner__content">
        <div class="cookie-banner__text">
          <p id="cookie-title" class="cookie-banner__title">{{ $t('cookie.title') }}</p>
          <p id="cookie-description" class="cookie-banner__description">
            {{ $t('cookie.description') }}
          </p>
        </div>

        <div class="cookie-banner__toggles">
          <label class="cookie-toggle">
            <input type="checkbox" checked disabled />
            <span>{{ $t('cookie.toggle[0]') }}</span>
          </label>

          <label class="cookie-toggle">
            <input v-model="preferences.analytics" type="checkbox" />
            <span>{{ $t('cookie.toggle[1]') }}</span>
          </label>

          <label class="cookie-toggle">
            <input v-model="preferences.marketing" type="checkbox" />
            <span>{{ $t('cookie.toggle[2]') }}</span>
          </label>
        </div>

        <div class="cookie-banner__actions">
          <button class="btn btn--ghost" type="button" @click="rejectAll">
            {{ $t('cookie.buttons[0]') }}
          </button>
          <button class="btn btn--secondary" type="button" @click="savePreferences">
            {{ $t('cookie.buttons[1]') }}
          </button>
          <button class="btn btn--primary" type="button" @click="acceptAll">
            {{ $t('cookie.buttons[2]') }}
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script>
export default {
  name: 'CookieBanner',
  props: {
    forceShow: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    forceShow: {
      immediate: true,
      handler(newVal) {
        if (newVal) this.visible = true
      }
    }
  },
  data() {
    return {
      STORAGE_KEY: 'cookie-consent',
      visible: false,
      preferences: {
        necessary: true,
        analytics: false,
        marketing: false,
      }
    }
  },
  mounted() {
    const saved = localStorage.getItem(this.STORAGE_KEY)

    if (!saved) {
      this.visible = true
      return
    }

    try {
      this.preferences = JSON.parse(saved)
    } catch {
      this.visible = true
    }
  },
  methods: {
    persistConsent(value) {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(value))
      this.preferences = value
      this.visible = false
      if (!value.analytics || !value.marketing) {
        this.clearUnwantedCookies()
      }
      this.$emit('consent-saved', value)
      this.$emit('close')

      // Verstuur een globaal event zodat main.js weet dat de scripts geladen mogen worden
      window.dispatchEvent(new CustomEvent('cookie-consent-changed', {
        detail: value
      }));
    },
    clearUnwantedCookies() {
      // Verwijder cookies van de huidige domein (localhost/vlecad)
      const cookies = document.cookie.split(";");
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i];
        const eqPos = cookie.indexOf("=");
        const name = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();
         // Lijst van Google Analytics en Freshworks CRM cookies die verwijderd moeten worden
        const cookiesToDelete = [
          '_ga', '_gid', '_gat', '_gac_', '_gcl_', // Google Analytics
          '_fw_crm_v', '_fw_crm_s' // Freshworks CRM (voorbeeld, pas aan indien nodig)
        ];

        // Controleer of de cookienaam begint met een van de te verwijderen prefixes
        if (cookiesToDelete.some(prefix => name.startsWith(prefix))) {
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
        }
      }
    },
    acceptAll() {
      this.persistConsent({
        necessary: true,
        analytics: true,
        marketing: true,
      })
    },
    rejectAll() {
      this.persistConsent({
        necessary: true,
        analytics: false,
        marketing: false,
      })
    },
    savePreferences() {
      this.persistConsent({
        ...this.preferences,
        necessary: true,
      })
    }
  }
}
</script>

<style scoped>
.cookie-banner {
  position: fixed;
  left: 50%;
  bottom: 24px;
  transform: translateX(-50%);
  width: min(92vw, 920px);
  z-index: 9999;
}

.cookie-banner__content {
  background: #111827;
  color: #f9fafb;
  border-radius: 18px;
  padding: 24px;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.cookie-banner__title {
  margin: 0 0 8px;
  font-size: 1.2rem;
  font-weight: 700;
}

.cookie-banner__description {
  margin: 0;
  color: #d1d5db;
  line-height: 1.6;
}

.cookie-banner__toggles {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin: 20px 0;
}

.cookie-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.06);
  padding: 10px 14px;
  border-radius: 999px;
}

.cookie-banner__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.btn {
  border: 0;
  border-radius: 12px;
  padding: 12px 16px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.15s ease, opacity 0.15s ease;
}

.btn:hover {
  transform: translateY(-1px);
}

.btn--ghost {
  background: transparent;
  color: #f9fafb;
  border: 1px solid rgba(255, 255, 255, 0.18);
}

.btn--secondary {
  background: #374151;
  color: #fff;
}

.btn--primary {
  background: #10b981;
  color: #052e26;
}

.fade-up-enter-active,
.fade-up-leave-active {
  transition: all 0.25s ease;
}

.fade-up-enter-from,
.fade-up-leave-to {
  opacity: 0;
  transform: translate(-50%, 12px);
}

@media (max-width: 640px) {
  .cookie-banner__content {
    padding: 18px;
  }

  .cookie-banner__actions {
    flex-direction: column;
  }

  .btn {
    width: 100%;
  }
}
</style>